import { Deferred, Head, router, usePage } from '@inertiajs/react'
import { ArrowsRightLeftIcon, BanknotesIcon } from '@heroicons/react/24/outline'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Card, CardTitle, Select, Table, TableHead, TableBody, TableRow, TableHeader, TableCell, Text } from '@/components/ui'
import { TransactionStatusBadge } from '@/components/common/TransactionStatusBadge'
import { MonoId } from '@/components/common/MonoId'
import { TextLink } from '@/components/common/TextLink'
import { GatewayNotice } from '@/components/common/GatewayNotice'
import { StatsSkeleton, CardSkeleton } from '@/components/common/Skeletons'
import { useTranslations } from '@/lib/i18n'
import { formatMoney, formatDate, formatDateTime } from '@/lib/format'
import type { GatewayState, GatewayError, PaymentRecord, SharedProps, StatusFamily } from '@/types'

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>

interface CurrencyTotals {
  volume: string
  refunded: string
  count: number
}

interface EurSummary {
  volume: string
  as_of: string
  source: string
  missing: string[]
}

interface Metrics {
  from: string
  to: string
  total_count: number
  sampled_count: number
  truncated: boolean
  currencies: Record<string, CurrencyTotals>
  by_family: Partial<Record<StatusFamily, number>>
  recent: PaymentRecord[]
  eur: EurSummary | null
}

interface DashboardProps {
  dashboard?: { metrics: Metrics | null; error: GatewayError | null }
  range: { from: string; to: string; days: number; options: number[] }
  gateway: GatewayState
}

function DashboardIndex({ dashboard, range, gateway }: DashboardProps) {
  const { t, locale } = useTranslations()
  const { props } = usePage<SharedProps>()
  const canManage = ['superadmin', 'admin'].includes(props.auth.user?.role ?? '')
  const manageHref = gateway.merchant ? `/merchants/${gateway.merchant.id}/edit` : undefined
  const metrics = dashboard?.metrics ?? null
  const currencies = metrics ? Object.entries(metrics.currencies) : []

  return (
    <>
      <Head title={t('dashboard.title')} />
      <PageHeading
        title={t('dashboard.title')}
        subtitle={t('dashboard.range', { from: formatDate(range.from, locale), to: formatDate(range.to, locale) })}
        actions={
          <Select
            aria-label={t('dashboard.range_label')}
            value={range.days}
            onChange={(e) => router.get('/dashboard', { days: e.target.value }, { preserveScroll: true })}
          >
            {range.options.map((days) => (
              <option key={days} value={days}>
                {t('dashboard.last_days', { count: days })}
              </option>
            ))}
          </Select>
        }
      />

      <div className="mt-6 flex flex-col gap-6">
        <GatewayNotice state={gateway} manageHref={canManage ? manageHref : undefined} createHref={canManage ? '/merchants' : undefined} />

        {gateway.configured && (
          <Deferred
            data="dashboard"
            fallback={
              <>
                <StatsSkeleton count={3} />
                <CardSkeleton lines={5} />
              </>
            }
          >
            {dashboard?.error ? (
              <GatewayNotice state={{ ...gateway, error: dashboard.error }} />
            ) : metrics ? (
              <>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  <Stat
                    label={t('dashboard.stats.transactions')}
                    value={String(metrics.total_count)}
                    icon={ArrowsRightLeftIcon}
                  />
                  {metrics.eur ? (
                    // EUR-unified total (ECB rates). Native per-currency figures live on the payments list.
                    <Stat
                      label={t('dashboard.stats.total_volume')}
                      value={formatMoney(metrics.eur.volume, 'EUR', locale)}
                      icon={BanknotesIcon}
                    />
                  ) : (
                    // FX unavailable — fall back to native per-currency volume.
                    currencies.map(([currency, totals]) => (
                      <Stat
                        key={`vol-${currency}`}
                        label={`${t('dashboard.stats.volume')} (${currency})`}
                        value={formatMoney(totals.volume, currency, locale)}
                        icon={BanknotesIcon}
                      />
                    ))
                  )}
                </div>

                <Text className="text-xs text-text/60">
                  {t('dashboard.completed_only')}
                  {metrics.eur &&
                    ` · ${t('dashboard.fx_basis', { date: formatDate(metrics.eur.as_of, locale) })}`}
                  {metrics.eur && metrics.eur.missing.length > 0 &&
                    ` · ${t('dashboard.fx_excludes', { currencies: metrics.eur.missing.join(', ') })}`}
                  {metrics.truncated &&
                    ` · ${t('dashboard.truncated', { sampled: metrics.sampled_count, total: metrics.total_count })}`}
                </Text>

                <Card>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('dashboard.recent')}</CardTitle>
                    <TextLink href="/payments" className="text-sm">
                      {t('dashboard.view_all')}
                    </TextLink>
                  </div>

                  {metrics.recent.length === 0 ? (
                    <Text className="mt-3">{t('payments.empty')}</Text>
                  ) : (
                    <Table className="mt-3">
                      <TableHead>
                        <TableRow>
                          <TableHeader>{t('payments.columns.date')}</TableHeader>
                          <TableHeader>{t('payments.columns.transaction')}</TableHeader>
                          <TableHeader>{t('payments.columns.status')}</TableHeader>
                          <TableHeader className="text-right">{t('payments.columns.amount')}</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {metrics.recent.map((p) => (
                          <TableRow key={p.transaction_id} href={`/payments/${p.transaction_id}`}>
                            <TableCell className="whitespace-nowrap">{formatDateTime(p.created_at, locale)}</TableCell>
                            <TableCell>
                              <MonoId value={p.transaction_id} />
                            </TableCell>
                            <TableCell>
                              <TransactionStatusBadge status={p.status} family={p.status_family} />
                            </TableCell>
                            <TableCell className="text-right font-medium text-text-strong">
                              {formatMoney(p.amount, p.currency, locale)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Card>
              </>
            ) : null}
          </Deferred>
        )}
      </div>
    </>
  )
}

function Stat({ label, value, icon: Icon }: { label: string; value: string; icon?: IconType }) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <p className="text-subhead font-semibold text-text">{label}</p>
        {Icon && <Icon aria-hidden="true" className="size-5 shrink-0 text-brand-turquoise" />}
      </div>
      <p className="mt-2 font-heading text-display font-bold tabular-nums text-text-strong">{value}</p>
    </Card>
  )
}

DashboardIndex.layout = withAppLayout

export default DashboardIndex
